import { supabase } from '../lib/supabase';

const intakeIsCurrentOrFuture = (intake) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const openingDate = intake?.application_opens_at;
  if (openingDate) {
    const parsedOpeningDate = new Date(openingDate);
    if (!Number.isNaN(parsedOpeningDate.getTime()) && parsedOpeningDate > today) return false;
  }
  const closingDate = intake?.application_closes_at;
  if (closingDate) {
    const parsedClosingDate = new Date(closingDate);
    if (!Number.isNaN(parsedClosingDate.getTime()) && parsedClosingDate < today) return false;
  }

  const academicYear = String(intake?.academic_year || intake?.name || '');
  const yearMatch = academicYear.match(/(\d{4})(?:\s*\/\s*(\d{2,4}))?/);
  if (yearMatch) {
    const startYear = Number(yearMatch[1]);
    const endYear = yearMatch[2]
      ? Number(yearMatch[2].length === 2 ? `${String(startYear).slice(0, 2)}${yearMatch[2]}` : yearMatch[2])
      : startYear;
    return endYear >= today.getFullYear();
  }

  return true;
};

export const admissionService = {
  async extractAcademicDocument(file, applicationContext = {}) {
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) {
      throw new Error(`${file.name} must be a PDF, JPG or PNG file no larger than 5 MB.`);
    }
    const fileData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('The selected document could not be read.'));
      reader.readAsDataURL(file);
    });
    const { data, error } = await supabase.functions.invoke('scan-admission-document', {
      body: { fileName: file.name, mimeType: file.type, sizeBytes: file.size, fileData, applicationContext }
    });
    if (error) return { status: 'manual_review', reason: 'Automatic extraction is temporarily unavailable. The admissions office will review this document.', extracted: {}, confidence: 0 };
    return data;
  },
  async getOpenPrograms() {
    const { data, error } = await supabase.rpc('get_open_admission_programs');
    if (error) throw error;
    return data || [];
  },

  async getOpenIntakes() {
    const syncResult = await supabase.rpc('get_open_admission_intakes');
    if (!syncResult.error) return (syncResult.data || []).filter(intakeIsCurrentOrFuture);
    if (!['PGRST202', '42883'].includes(syncResult.error.code)) throw syncResult.error;

    const { data, error } = await supabase.from('admission_intakes').select('*').order('application_opens_at');

    if (error) throw error;
    return (data || []).filter(intakeIsCurrentOrFuture);
  },

  async trackApplication(applicationNumber, email) {
    const { data, error } = await supabase.rpc('track_admission_application', {
      lookup_application_number: applicationNumber.trim().toUpperCase(),
      lookup_email: email.trim().toLowerCase()
    });
    if (error) throw error;
    const result = Array.isArray(data) ? data[0] : data;
    if (!result) throw new Error('No application matched that application code and email address.');
    return result;
  },

  async lookupKuccpsPlacement({ kcseIndexNumber, kcseYear, placementReference }) {
    const { data, error } = await supabase.rpc('lookup_kuccps_placement', {
      index_number: kcseIndexNumber, exam_year: Number(kcseYear), placement_ref: placementReference
    });
    if (error) throw error;
    if (!data?.length) throw new Error('No matching KUCCPS placement was found. Confirm the index number, year and placement reference.');
    return data[0];
  },

  async claimKuccpsPlacement(payload) {
    const { data, error } = await supabase.rpc('claim_kuccps_placement', { payload });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  async importKuccpsPlacements({ fileName, placementYear, rows, programs }) {
    const { data: authData } = await supabase.auth.getUser();
    const { data: batch, error: batchError } = await supabase.from('kuccps_import_batches').insert({
      file_name: fileName, placement_year: Number(placementYear), row_count: rows.length, imported_by: authData.user.id
    }).select().single();
    if (batchError) throw batchError;
    const programByCode = new Map(programs.map(program => [String(program.code).toUpperCase(), program]));
    const placements = rows.map((row, index) => {
      const program = programByCode.get(String(row.program_code || '').toUpperCase());
      if (!program) throw new Error(`Row ${index + 2}: unknown programme code ${row.program_code}`);
      return {
        import_batch_id: batch.id, kcse_index_number: String(row.kcse_index_number).trim(), kcse_year: Number(row.kcse_year),
        placement_reference: String(row.placement_reference).trim(), placement_year: Number(row.placement_year || placementYear),
        first_name: String(row.first_name).trim(), middle_name: row.middle_name ? String(row.middle_name).trim() : null,
        last_name: String(row.last_name).trim(), gender: row.gender || null, program_id: program.id,
        placed_level: Number(String(row.placed_level).match(/[3-7]/)?.[0])
      };
    });
    for (let index = 0; index < placements.length; index += 200) {
      const { error } = await supabase.from('kuccps_placements').upsert(placements.slice(index, index + 200), { onConflict: 'kcse_index_number,kcse_year,placement_year' });
      if (error) throw error;
    }
    return { batch, imported: placements.length };
  },

  async getProgramAdmissionLevels(programId, intakeId) {
    if (!programId || !intakeId) return [];
    const { data, error } = await supabase.rpc('get_open_program_admission_levels', {
      target_program: programId,
      target_intake: intakeId
    });
    if (error) throw error;
    return data || [];
  },

  async prepareApplication(application, documents = []) {
    const { documents: ignoredDocuments, ...payload } = application;
    if (documents.length > 1) throw new Error('Combine all academic documents into one file no larger than 5 MB.');
    for (const document of documents) {
      if (!['application/pdf','image/jpeg','image/png'].includes(document.file.type) || document.file.size <= 0 || document.file.size > 5 * 1024 * 1024) {
        throw new Error(`${document.file.name} must be a non-empty PDF, JPG or PNG file no larger than 5 MB.`);
      }
    }
    const appliedLevel = Number(payload.applied_level);
    if (!payload.program_id || !Number.isInteger(appliedLevel)) {
      throw new Error('Select a valid programme and level before continuing.');
    }
    payload.applied_level = appliedLevel;
    payload.kcse_year = payload.kcse_year ? Number(payload.kcse_year) : null;
    payload.kuccps_placement_year = payload.kuccps_placement_year ? Number(payload.kuccps_placement_year) : null;
    const { data, error } = await supabase.rpc('submit_admission_application', {
      payload
    });

    if (error) throw error;
    const draft = Array.isArray(data) ? data[0] : data;

    const { error: disabilityError } = await supabase.rpc('set_admission_disability', {
      target_application: draft.application_id, target_application_number: draft.application_number,
      disability_flag: Boolean(payload.has_disability), disability_kind: payload.disability_nature || null,
      disability_description: payload.disability_details || null
    });
    if (disabilityError) throw disabilityError;

    for (const document of documents) {
      const safeName = document.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${draft.application_id}/${crypto.randomUUID()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('admission-quarantine')
        .upload(storagePath, document.file, { contentType: document.file.type, upsert: false });
      if (uploadError) throw uploadError;

      const { data: documentId, error: registerError } = await supabase.rpc('register_admission_document', {
        target_application: draft.application_id,
        target_application_number: draft.application_number,
        target_document: {
          document_type: document.type,
          storage_path: storagePath,
          original_name: document.file.name,
          mime_type: document.file.type,
          size_bytes: document.file.size
          ,extracted_data: document.extractedData || {}
          ,applicant_edited_fields: document.editedFields || []
        }
      });
      if (registerError) {
        await supabase.storage.from('admission-quarantine').remove([storagePath]);
        throw registerError;
      }

      const { data: scan, error: scanError } = await supabase.functions.invoke('scan-admission-document', {
        body: {
          applicationId: draft.application_id,
          applicationNumber: draft.application_number,
          documentId
        }
      });
      if (scanError) continue;
      if (scan?.status === 'rejected') throw new Error(`${document.file.name} was not accepted: ${scan?.reason || 'invalid or unsafe file.'}`);
    }

    return draft;
  },

  async finalizeApplication(draft) {
    const { error: finalizeError } = await supabase.rpc('finalize_admission_application', {
      target_application: draft.application_id,
      target_application_number: draft.application_number,
      target_declaration_accepted: true
    });
    if (finalizeError) throw finalizeError;
    return draft;
  },

  async submitApplication(application, documents = []) {
    const draft = await this.prepareApplication(application, documents);
    return this.finalizeApplication(draft);
  },

  async createWalkInAdmission(application) {
    const { data, error } = await supabase.rpc('create_walk_in_admission', { payload:{...application,application_type:'self_sponsored',declaration_accepted:true} });
    if (error) throw error;
    const result=Array.isArray(data)?data[0]:data;
    supabase.functions.invoke('send-admission-notification',{body:{applicationId:result.application_id}}).catch(()=>{});
    return result;
  },

  async getApplications(filters = {}) {
    let query = supabase
      .from('admission_applications')
      .select('*, intake:intake_id(id, code, name, academic_year), documents:admission_documents(id,document_type,detected_document_type,scan_status,scan_reason,security_status,security_reason,sha256,extraction_confidence,extracted_data,applicant_edited_fields,requires_manual_review)', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') query = query.eq('status', filters.status);
    if (filters.applicationType && filters.applicationType !== 'all') {
      query = query.eq('application_type', filters.applicationType);
    }
    // "all" is the UI sentinel, not an intake UUID. Never pass it to the
    // uuid-typed intake_id column or Postgres will reject the query.
    if (filters.intakeId && filters.intakeId !== 'all') {
      query = query.eq('intake_id', filters.intakeId);
    }

    const [{ data, error, count }, { data: existingStudents, error: studentsError }, { data: programmeRows, error: programmesError }] = await Promise.all([
      query,
      supabase
        .from('students')
        .select('profile_id,source_application_id,admission_number,program_id,first_name,middle_name,last_name,personal_email,institutional_email,email,phone_number,date_of_birth,gender,nationality,id_number,academic_level,intake_code,status,enrollment_status,enrollment_date,created_at')
        .order('created_at', { ascending: false }),
      supabase.from('programs').select('id,name,code,department_id')
    ]);
    if (error) throw error;
    if (studentsError) throw studentsError;
    if (programmesError) throw programmesError;

    const programmeMap = Object.fromEntries((programmeRows || []).map(programme => [String(programme.id), programme]));
    const applications = (data || []).map(application => ({...application,program:programmeMap[String(application.program_id)] || null}));
    const applicationIds = new Set(applications.map(item => item.id));
    const registrationNumbers = new Set(applications.map(item => item.registration_number).filter(Boolean));
    const studentOnlyRows = (existingStudents || [])
      .filter(student => !student.source_application_id || !applicationIds.has(student.source_application_id))
      .filter(student => !student.admission_number || !registrationNumbers.has(student.admission_number))
      .map(student => ({
        id: `student:${student.profile_id}`,
        _studentOnly: true,
        application_number: student.admission_number || `STUDENT-${String(student.profile_id).slice(0, 8).toUpperCase()}`,
        registration_number: student.admission_number,
        application_type: 'self_sponsored',
        sponsorship_category: 'existing_student',
        program_id: student.program_id,
        program: programmeMap[String(student.program_id)] || null,
        applied_level: Number(String(student.academic_level || '').match(/[3-7]/)?.[0]) || null,
        first_name: student.first_name,
        middle_name: student.middle_name,
        last_name: student.last_name,
        email: student.personal_email || student.email,
        student_email: student.institutional_email || student.email,
        phone_number: student.phone_number,
        date_of_birth: student.date_of_birth,
        gender: student.gender,
        nationality: student.nationality,
        national_id: student.id_number,
        status: 'admitted',
        submitted_at: student.enrollment_date || student.created_at,
        created_at: student.created_at,
        intake: { code: student.intake_code, name: student.intake_code || 'Existing student record' },
        documents: []
      }));

    const includeStudentRows = (!filters.status || filters.status === 'all' || filters.status === 'admitted')
      && (!filters.applicationType || filters.applicationType === 'all' || filters.applicationType === 'self_sponsored');
    const merged = includeStudentRows ? [...applications, ...studentOnlyRows] : applications;
    return { data: merged, count: (count || 0) + (includeStudentRows ? studentOnlyRows.length : 0) };
  },

  async updateApplicationStatus(id, status, reviewNotes = '') {
    const { data: userData } = await supabase.auth.getUser();
    const reviewFields = {
      review_notes: reviewNotes || null,
      reviewed_at: new Date().toISOString(),
      reviewed_by: userData?.user?.id || null,
      updated_at: new Date().toISOString()
    };

    // Compatibility with databases that still enforce the legacy offered -> admitted path.
    // The authoritative v29 migration removes that restriction entirely.
    if (status === 'admitted') {
      const { data: current, error: currentError } = await supabase
        .from('admission_applications')
        .select('status')
        .eq('id', id)
        .single();
      if (currentError) throw currentError;
      if (current.status !== 'offered' && current.status !== 'admitted') {
        const { error: offerError } = await supabase
          .from('admission_applications')
          .update({ ...reviewFields, status: 'offered' })
          .eq('id', id);
        if (offerError) throw offerError;
      }
    }

    const { data, error } = await supabase
      .from('admission_applications')
      .update({
        status,
        ...reviewFields
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (status === 'admitted') {
      const { error: provisionError } = await supabase.functions.invoke('provision-admitted-student', { body: { applicationId: id } });
      if (provisionError) throw new Error(`The student was admitted, but their portal account could not be created: ${provisionError.message}`);
    }
    supabase.functions.invoke('send-admission-notification',{body:{applicationId:id}}).catch(()=>{});
    return data;
  },

  async getApplicationDocuments(applicationId) {
    const { data, error } = await supabase
      .from('admission_documents')
      .select('*')
      .eq('application_id', applicationId)
      .order('created_at');
    if (error) throw error;
    return data || [];
  },

  async getLevelConfigurations(programId) {
    const { data, error } = await supabase.from('program_admission_levels').select('*').eq('program_id', programId).order('level');
    if (error) throw error;
    return data || [];
  },

  async saveLevelConfiguration(configuration) {
    const { data, error } = await supabase.from('program_admission_levels').upsert(configuration, { onConflict: 'program_id,level' }).select().single();
    if (error) throw error;
    return data;
  },

  async getDocumentUrl(storagePath) {
    const { data, error } = await supabase.storage
      .from('admission-quarantine')
      .createSignedUrl(storagePath, 300);
    if (error) throw error;
    return data.signedUrl;
  },

  // Fetch full admission letter data for an already-admitted student
  // using their source application ID (avoids needing the email lookup)
  async getAdmissionLetterData(applicationId) {
    // Fetch application + related tables (no institution_settings join — not a FK relation)
    const [appResult, settingsResult] = await Promise.all([
      supabase
        .from('admission_applications')
        .select('id, application_number, registration_number, status, first_name, middle_name, last_name, email, phone_number, applied_level, reviewed_at, updated_at, admitted_at, program_id, intake:intake_id(id, code, name, academic_year, reporting_date)')
        .eq('id', applicationId)
        .maybeSingle(),
      supabase
        .from('institution_settings')
        .select('name, motto, logo_url, phone, email, website, postal_address, address, locale, registrar_name, registrar_title, registrar_signature_url, admissions_office_name, admissions_email, admissions_phone, official_reference_prefix, admission_letter_footer')
        .limit(1)
        .maybeSingle(),
    ]);

    if (appResult.error) return { data: null, error: appResult.error };
    if (!appResult.data) return { data: null, error: { message: 'Admission application not found.' } };

    const appData = appResult.data;
    const settings = settingsResult.data || {};

    // Look up program details
    const { data: programData } = await supabase
      .from('programs')
      .select('name')
      .eq('id', appData.program_id)
      .maybeSingle();

    // Look up admitted student's institutional email
    const { data: student } = await supabase
      .from('students')
      .select('institutional_email, admission_number')
      .eq('source_application_id', applicationId)
      .maybeSingle();

    const letterData = {
      application_number: appData.application_number,
      registration_number: appData.registration_number || student?.admission_number,
      applicant_name: [appData.first_name, appData.middle_name, appData.last_name].filter(Boolean).join(' '),
      program_name: programData?.name,
      applied_level: appData.applied_level,
      intake_name: appData.intake?.name,
      academic_year: appData.intake?.academic_year,
      reporting_date: appData.intake?.reporting_date,
      offered_at: appData.reviewed_at || appData.updated_at || appData.admitted_at,
      student_email: student?.institutional_email,
      status: appData.status,
      // Map physical columns to the expected aliases used by the PDF generator
      institution_name: settings.name,
      institution_motto: settings.motto,
      institution_logo_url: settings.logo_url,
      institution_phone: settings.phone,
      institution_email: settings.email,
      institution_website: settings.website,
      institution_postal_address: settings.postal_address,
      institution_address: settings.address,
      institution_locale: settings.locale,
      registrar_name: settings.registrar_name,
      registrar_title: settings.registrar_title,
      registrar_signature_url: settings.registrar_signature_url,
      admissions_office_name: settings.admissions_office_name,
      admissions_email: settings.admissions_email,
      admissions_phone: settings.admissions_phone,
      letter_reference_prefix: settings.official_reference_prefix,
      admission_letter_footer: settings.admission_letter_footer,
    };
    return { data: letterData, error: null };
  },

  async getIntakes() {
    const syncResult = await supabase.rpc('sync_admission_intakes_from_calendar');
    if (syncResult.error && !['PGRST202', '42883'].includes(syncResult.error.code)) throw syncResult.error;
    let { data, error } = await supabase
      .from('admission_intakes')
      .select('*, academic_year_ref:academic_year_id(id,name,start_date,end_date), semester_ref:semester_id(id,name,term_number,start_date,end_date,status)')
      .order('application_opens_at', { ascending: false, nullsFirst: false });
    if (error && ['PGRST200', '42703'].includes(error.code)) {
      const fallback = await supabase.from('admission_intakes').select('*').order('application_opens_at', { ascending: false, nullsFirst: false });
      data = fallback.data;
      error = fallback.error;
    }
    if (error) throw error;
    return (data || []).filter((intake) => {
      const endDate = intake.semester_ref?.end_date || intake.academic_year_ref?.end_date;
      if (!endDate) return intakeIsCurrentOrFuture(intake);
      const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Number.isNaN(end.getTime()) || end >= today;
    });
  },

  async createIntake(intake) {
    const { data, error } = await supabase
      .from('admission_intakes')
      .insert({
        code: intake.code.trim().toUpperCase(),
        name: intake.name.trim(),
        academic_year: intake.academic_year.trim(),
        application_opens_at: intake.application_opens_at || null,
        application_closes_at: intake.application_closes_at || null,
        reporting_date: intake.reporting_date || intake.semester_start_date || null,
        supported_channels: intake.supported_channels || ['self_sponsored', 'kuccps'],
        is_active: true,
        academic_year_id: intake.academic_year_id || null,
        semester_id: intake.semester_id || null,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateIntake(id, intake) {
    const { data, error } = await supabase
      .from('admission_intakes')
      .update({
        application_opens_at: intake.application_opens_at || null,
        application_closes_at: intake.application_closes_at || null,
        is_active: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteIntake(id) {
    const { error } = await supabase.from('admission_intakes').delete().eq('id', id);
    if (error) throw error;
  },
};

export default admissionService;
